import React, { useEffect, useState } from "react";
import { Button, Select, Spin, message } from "antd";
import { createFinancier, createFinancierAlias, getAllFinanciers, updateException } from "../api";
import { ExceptionStatus } from "../ExceptionDrawer";
import Hide from "../../components/Hide";

const { Option } = Select;

const FinancierComponent = ({ exception, onClose, onResolved }) => {
    const [financierName, setFinancierName] = useState(null);
    const [showSelect, setShowSelect] = useState(false);
    const [financiers, setFinanciers] = useState([]);
    const [selectedAliasId, setSelectedAliasId] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        setFinancierName(exception?.unknown_infomation || null);

        const fetchFinanciers = async () => {
            setLoading(true);
            try {
                const response = await getAllFinanciers();
                const financiersData = response?.data?.data || response?.data || [];
                setFinanciers(Array.isArray(financiersData) ? financiersData : []);
            } catch (error) {
                console.error("Error fetching financiers", error);
                message.error("Failed to fetch financiers");
            } finally {
                setLoading(false);
            }
        };

        fetchFinanciers();
    }, [exception]);

    const handleMarkAsNew = async () => {
        if (!financierName) {
            onClose();
            return;
        }

        if (exception.status !== ExceptionStatus.PENDING) {
            try { if (typeof onResolved === 'function') await onResolved(); } catch {}
            onClose();
            return;
        }

        try {
            setLoading(true);
            await createFinancier({ name: financierName });
            await updateException(exception.id);
            message.success("Financier created");
        } catch (error) {
            console.error(`Failed to create financier "${financierName}".`, error);
            message.error("Failed to create financier");
        } finally {
            setLoading(false);
            try { if (typeof onResolved === 'function') await onResolved(); } catch {}
            onClose();
        }
    };

    const handleMarkAsAliasFor = () => {
        setShowSelect(true);
    };

    const handleFinancierSelect = (value) => {
        setSelectedAliasId(value ?? null);
    };

    const handleSaveAlias = async () => {
        if (!selectedAliasId || !financierName) return;

        if (exception.status !== ExceptionStatus.PENDING) {
            onClose();
            return;
        }

        try {
            setLoading(true);
            await createFinancierAlias({ name: financierName, alias_for: selectedAliasId });
            await updateException(exception.id);
            message.success("Alias saved");
        } catch (error) {
            console.error(`Failed to create alias for financier "${financierName}".`, error);
            message.error("Failed to save alias");
        } finally {
            setLoading(false);
            try { if (typeof onResolved === 'function') await onResolved(); } catch {}
            onClose();
        }
    };

    return (
        <div style={{ marginTop: 20 }}>
            <Hide condition={exception.status === ExceptionStatus.RESOLVED}>
                <Spin spinning={loading} tip="Processing...">
                    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                        <Button
                            type="primary"
                            onClick={handleMarkAsNew}
                            disabled={loading}
                            loading={loading}
                        >
                            Mark as New
                        </Button>
                        <Button
                            type="default"
                            onClick={handleMarkAsAliasFor}
                            disabled={loading}
                        >
                            Mark as Alias For
                        </Button>
                        {showSelect && (
                            <>
                                <div style={{ fontSize: 12, color: '#666' }}>
                                    {financierName ? `Select an existing financier to alias "${financierName}" to.` : 'Select an existing financier to alias.'}
                                </div>
                                <Select
                                    style={{ minWidth: 220 }}
                                    placeholder="Select a financier"
                                    onChange={handleFinancierSelect}
                                    disabled={loading}
                                    value={selectedAliasId ?? undefined}
                                >
                                    {financiers.map((financier) => (
                                        <Option key={financier.id} value={financier.id}>
                                            {financier.name}
                                        </Option>
                                    ))}
                                </Select>
                                <Button
                                    type="primary"
                                    onClick={handleSaveAlias}
                                    disabled={loading || !selectedAliasId}
                                    loading={loading}
                                >
                                    Save
                                </Button>
                            </>
                        )}
                    </div>
                </Spin>
            </Hide>
        </div>
    );
};

export default FinancierComponent;

